import type { Call } from '@entities/call';
import { CallStatuses, CallTypes } from '@entities/call';
import type { BaseUsecase } from '@usecases/base-usecase';

export type TransferNumberRequest = {
  callId: string;
  to: string;
  name?: string;
};

export interface TransferNumberRepository {
  getCurrentCall(): Promise<Call>;
  insertOutboundCall(call: Call): Promise<void>;
  updateHoldStatus(id: string, isOnHold: boolean): Promise<void>;
}

export interface TransferNumberUserRepository {
  getOutboundNumber(): Promise<string>;
}

export interface TransferNumberEventPublisher {
  publish(call: Call): Promise<void>;
}

export interface TransferNumberUsecase
  extends BaseUsecase<TransferNumberRequest, void> {
  run(call: TransferNumberRequest): Promise<void>;
}

export class TransferNumber implements TransferNumberUsecase {
  constructor(
    private userRepository: TransferNumberUserRepository,
    private repository: TransferNumberRepository,
    private publisher: TransferNumberEventPublisher,
  ) {}

  async run({ callId: id, to, name }: TransferNumberRequest): Promise<void> {
    const from = await this.userRepository.getOutboundNumber();
    const currentCall = await this.repository.getCurrentCall();
    const call: Call = {
      id,
      to,
      from,
      type: CallTypes.ADD_PARTICIPANT,
      status: CallStatuses.DIALING,
      isMuted: false,
      isOnHold: false,
      name,
    };

    await Promise.all([
      this.publisher.publish(call),
      this.repository.insertOutboundCall(call),
      currentCall && this.repository.updateHoldStatus(currentCall.id, true),
    ]);
  }
}
